import React from 'react';
import { isEmpty } from 'lodash';
import {
  CardHcd,
  ProfessionalHcd,
} from '@maiia/model/generated/model/api-patient/api-patient';

import { asyncActions } from '@docavenue/core';
import { useDispatch, useSelector } from 'react-redux';
import { CardSection, IconPractitioner } from '@maiia/design-system';
import { Typography } from '@mui/material';
import Link from '../../atoms/Link/Link';

import { useTranslation } from '@/src/i18n';
import { getPicture } from '../../utils/card';
import Avatar from '../../atoms/Avatar/Avatar';

import { tlcCardsActions } from '../../../src/actions';
import { isDemoAccountSelector } from '@/src/selectors';
import { upperCaseFirstLetter } from '@/src/utils';

type PractitionerComponentProps = {
  t: any;
  isOnline?: boolean;
  practitioner: {
    firstName: string;
    lastName: string;
    avatarThumbnailS3Id?: string;
    professional: ProfessionalHcd;
    speciality: { isParamedical: boolean };
  };
};

type Props = {
  card: CardHcd;
};

const PractitionerComponent = ({
  practitioner,
  isOnline,
  t,
}: PractitionerComponentProps) => (
  <>
    <Avatar
      size="big"
      source={getPicture(practitioner.avatarThumbnailS3Id)}
      isOnline={isOnline}
    />
    <span className="first">
      {`${
        practitioner?.speciality?.isParamedical === false
          ? t('professional_title')
          : ''
      }${upperCaseFirstLetter(practitioner.firstName)}`}
    </span>
    <span>{`${practitioner.lastName}`}</span>
  </>
);

const PractitionerList = ({ card }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [pratListOnline, setPratListOnline] = React.useState<string[]>([]);
  const practitioners = (card?.center?.practitioners ?? [])
    .filter(prat => prat?.isVisibleOnline && !prat?.isSecret)
    .reduce((result, practitioner) => {
      const specialityName = practitioner.speciality?.name || '';
      const practitionersBySpec = result[specialityName] || [];
      practitionersBySpec.push(practitioner);
      return { ...result, [specialityName]: practitionersBySpec };
    }, {});

  const isDemoAccount = useSelector(isDemoAccountSelector);
  const isDemoListPractitioner = practitionersList =>
    !isDemoAccount
      ? practitionersList.filter(p => !p.isDemo)
      : practitionersList;

  const practitionerIds = (card?.center?.practitioners ?? []).map(
    item => item.id,
  );

  React.useEffect(() => {
    asyncActions(
      dispatch,
      tlcCardsActions.getActions().getList({
        practitionerIds,
      }),
    ).then((res: any) =>
      setPratListOnline(
        res.items.map(item => item.professional.practitionerId),
      ),
    );
  }, []);

  return (
    <CardSection
      icon={<IconPractitioner />}
      title={t('center_practitioner_list')}
    >
      {!isEmpty(practitioners) &&
        Object.keys(practitioners).map(speciality => (
          <div className="center-specialty" key={speciality}>
            <Typography component="h3" variant="h6">
              {speciality}
            </Typography>
            <ul className="center-prat-list">
              {isDemoListPractitioner(practitioners[speciality]).map(
                practitioner => (
                  <li key={`${practitioner.id}`}>
                    {practitioner.url ? (
                      <Link
                        href={`${practitioner.url}?centerId=${card?.center?.id}`}
                        passHref
                        legacyBehavior
                      >
                        <a>
                          <PractitionerComponent
                            t={t}
                            practitioner={practitioner}
                            isOnline={pratListOnline.includes(practitioner.id)}
                          />
                        </a>
                      </Link>
                    ) : (
                      <PractitionerComponent
                        t={t}
                        practitioner={practitioner}
                      />
                    )}
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}
    </CardSection>
  );
};

export default PractitionerList;
